import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';
import { connectorKeys } from 'api/connectors';

export const oauthKeys = {
  providers: ['oauth-providers'],
  grants: ['oauth-grants'],
  grantsFor: (provider, connectorTypeSlug) => ['oauth-grants', { provider, connectorTypeSlug }],
  resources: (grantId, kind) => ['oauth-grants', grantId, 'resources', kind],
};

// Text for the fixed error codes the callback redirects with (oauth.services.ERROR_*).
export const OAUTH_ERROR_TEXT = {
  denied: 'You declined access at the provider, so nothing was connected.',
  expired: 'The sign-in took too long and the request expired. Start it again.',
  exchange_failed:
    'The provider did not accept the sign-in. Try again; if it keeps failing, contact support.',
  provider_disabled: 'This provider is not enabled on the platform yet.',
  invalid_state:
    'The sign-in could not be verified. Start it again from this page rather than from a bookmark.',
};

export function oauthErrorText(code) {
  return OAUTH_ERROR_TEXT[code] || 'The account could not be connected. Please try again.';
}

export function useOAuthProviders() {
  return useQuery({
    queryKey: oauthKeys.providers,
    queryFn: () => apiClient.get('/api/v1/oauth/providers/').then((r) => r.data),
    staleTime: 1000 * 60 * 60,
  });
}

// With a connector type named, the server narrows the list to its provider
// and adds `missing_scopes` per account.
export function useOAuthGrants(provider, connectorTypeSlug) {
  const params = {
    ...(provider ? { provider } : {}),
    ...(connectorTypeSlug ? { connector_type: connectorTypeSlug } : {}),
  };
  const scoped = provider || connectorTypeSlug;
  return useQuery({
    queryKey: scoped ? oauthKeys.grantsFor(provider, connectorTypeSlug) : oauthKeys.grants,
    queryFn: () => apiClient.get('/api/v1/oauth/grants/', { params }).then((r) => r.data),
    // A platform-side refresh can break a grant at any time.
    refetchInterval: 60000,
  });
}

export function useStartOAuth() {
  return useMutation({
    mutationFn: ({ provider, connectorTypeSlug, returnTo }) =>
      apiClient
        .post(`/api/v1/oauth/${provider}/start/`, {
          ...(connectorTypeSlug ? { connector_type_slug: connectorTypeSlug } : {}),
          ...(returnTo ? { return_to: returnTo } : {}),
        })
        .then((r) => r.data),
  });
}

export function useRevokeGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId) =>
      apiClient.post(`/api/v1/oauth/grants/${grantId}/revoke/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: oauthKeys.grants });
      // Revoking takes the grant's connections offline server-side.
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
    },
  });
}

// A failure is a result ({resources: [], error: <code>}), not an exception.
export function useGrantResources(grantId, kind) {
  return useQuery({
    queryKey: oauthKeys.resources(grantId, kind),
    queryFn: () =>
      apiClient
        .get(`/api/v1/oauth/grants/${grantId}/resources/`, { params: { kind } })
        .then((r) => r.data),
    enabled: !!grantId && !!kind,
    staleTime: 1000 * 60 * 5,
  });
}

// The consent is a full redirect that unloads the SPA, so the form draft is
// parked in sessionStorage and restored when the callback lands.
const draftKey = (provider) => `oauth-draft:${provider}`;

export function stashOAuthDraft(provider, draft) {
  try {
    sessionStorage.setItem(draftKey(provider), JSON.stringify(draft || {}));
  } catch {
    // storage unavailable (private mode): the consent still works
  }
}

export function takeOAuthDraft(provider) {
  try {
    const raw = sessionStorage.getItem(draftKey(provider));
    if (raw) sessionStorage.removeItem(draftKey(provider));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function goToConsent(authorizeUrl) {
  window.location.assign(authorizeUrl);
}
